/**
 * Unified Login helper functions
 * Contains logic for initializing and managing unified login flows
 */
import fetch from 'globals/wa-fetch';
import { lookupKeys, performTenancyLookup } from 'globals/regional-api';
import { setUser } from 'stores/user-store';
import { fetchCompany, setCompany } from 'stores/company-store';
import { updateFeaturesList } from 'state/features-list/actions';
import { getStore } from 'hermes-redux';
import * as Storage from 'globals/storage';
import { setSessionLoadingCompleted, updateUser } from 'state/session/actions';
import { handleUnexpectedAuthError } from 'app/run-with-usl.auth-helpers';
import {
  goTo,
  getUrlParameter,
  getSubdomainFromHost,
  getLandingPageUrl,
} from 'router/navigation';
import { runOnWebWorker } from 'app/run-on-web-worker';
import { startPostLoginFlow } from 'state/post-login/actions';
import { SIGNUP_CODES } from 'pages/signup/constants';
import { updateData } from 'pages/signup/main/state/actions';
import {
  AppLogger,
  DynatraceClient,
  LogTypes,
} from '@workivate/tho-web-shared';
import queryString from 'query-string';
import {
  setRedirectionModel,
  shouldResumePostLoginFlow,
} from 'app/state/consent/actions';
import Language from 'language';
import {
  EXCLUDE_FROM_ORIGINAL_PATH,
  PRESERVE_ON_ROOT_PATH,
} from 'app/run-with-usl.constants';
import { isPublicRoute } from 'router/helpers';
import { SIGN_UP_CONSENT_MODULE_NAME } from 'pages/consent/constants';
import { setHotjarUserAttributes } from 'globals/hotjar/helpers';

export const handleLangUpdateDuringLogin = async user => {
  const langQueryString = getUrlParameter('lang');
  const { dispatch } = getStore();

  /*
   * By default setLoginLanguagePersistence is set to false unless the user changes language.
   * If the 'shouldPersistLoginPageLanguage' storage value is true we will need to make an API call to permanently set the user locale
   * and reload translations.
   */
  if (Language.getLoginLanguagePersistence()) {
    // If the stored local is not the same as the user locale, we need to update it (unless it's a shared account)
    if (Language.getLocale() !== user.locale) {
      // For all user types except for shared users we should update the user locale - See UM-1695 for the original issue
      if (!user.is_shared_account) {
        await dispatch(
          updateUser({ locale: Language.getLocale() }, false, false),
        );
      }

      Language.loadTranslations(Language.getLocale());
    }
  } else {
    // If the locale should not be persisted, we will perform a one-time update
    await Language.set({
      locale: langQueryString || user.locale,
      isSharedAccount: user.is_shared_account,
      shouldLoadAllTranslations: true,
    });
  }

  setHotjarUserAttributes({ locale: user.locale });
};

/**
 * Sets the current path (pathname + filtered search params + hash) for redirect after login or signup
 * Uses browser URL utilities for robust URL handling and filtering
 * Filters out authentication-related parameters using EXCLUDED_REDIRECT_QUERY_PARAMS
 */
export const setOriginalPath = () => {
  try {
    const urlParams = queryString.parse(location.search);

    // Check if originalPath was passed as a query parameter (from subdomain redirect)
    // after having been stored on a generic subdomain by this same function
    // The originalPath will have been sanitized before being stored (below)
    // so we trust the parameter value will not be root or a public path and do not revalidate here
    if (urlParams.originalPath) {
      Storage.set(
        Storage.storageKeys.originalPath,
        urlParams.originalPath,
        false,
      );
      return; // We don't need to remove originalPath from the URL. goTo() will handle it when navigating
    }

    const url = new URL(window.location.href);

    // Remove excluded authentication parameters
    EXCLUDE_FROM_ORIGINAL_PATH.forEach(param => {
      url.searchParams.delete(param);
    });

    const pathname = url.pathname;

    // Special handling for root path: preserve specific query params
    // Storing a root path may cause issues
    // but we need to preserve certain params for external authentication flows (e.g., Zendesk)
    if (pathname === '/') {
      const paramsToPreserve: Record<string, string> = {};

      PRESERVE_ON_ROOT_PATH.forEach(param => {
        const value = url.searchParams.get(param);
        if (value) {
          paramsToPreserve[param] = value;
        }
      });

      // Store preserved params if any exist
      if (Object.keys(paramsToPreserve).length > 0) {
        Storage.set(
          Storage.storageKeys.preservedRootParams,
          JSON.stringify(paramsToPreserve),
          false,
        );
      }

      return; // Still skip storing root paths
    }

    // Don't save public routes (auth/signup flows, error pages, etc.)
    // we do not want to redirect back to /signup if the user followed a route to /signup
    if (isPublicRoute(pathname)) {
      return;
    }

    // Get the relative path (pathname + filtered search params + hash)
    const relativePath = url.href.substring(url.origin.length);

    // Store the relative path for post-login redirect
    Storage.set(Storage.storageKeys.originalPath, relativePath, false);
  } catch (error) {
    AppLogger.logWithOptions({
      message: 'Failed to parse current URL for path storage',
      level: LogTypes.Error,
      error: error,
      context: { url: window.location.href },
      shouldLogToDatadog: true,
      shouldLogToDynatrace: true,
    });
  }
};

/**
 * Gets and clears the stored original path
 * Returns the stored path and removes it from storage (one-time use)
 * Also checks for preserved root params (e.g., return_to for Zendesk) and reconstructs the URL
 */
export const getAndClearOriginalPath = () => {
  const originalPath: string =
    Storage.get(Storage.storageKeys.originalPath, false) || '';
  const preservedParamsJson: string =
    Storage.get(Storage.storageKeys.preservedRootParams, false) || '';

  // Clear both storage items after reading (one-time use)
  Storage.remove(Storage.storageKeys.originalPath);
  Storage.remove(Storage.storageKeys.preservedRootParams);

  // Prioritize originalPath (which already includes any query params from that page)
  if (originalPath) {
    return originalPath;
  }

  // Only use preserved root params if no originalPath exists
  if (preservedParamsJson) {
    try {
      const preservedParams = JSON.parse(preservedParamsJson);
      const queryParams = new URLSearchParams(preservedParams);
      return `/?${queryParams.toString()}`;
    } catch (error) {
      AppLogger.logWithOptions({
        message: 'Failed to parse preserved root params',
        level: LogTypes.Error,
        error: error,
        context: { preservedParamsJson },
        shouldLogToDatadog: true,
        shouldLogToDynatrace: true,
      });
    }
  }

  return '';
};

/**
 * Gets the redirect path after authentication (login or signup) completion
 * Checks for a saved originalPath first, then falls back to feature-based landing page
 * Note the landing page will be a route name and must be used with goTo()
 * (goTo() accepts both pathnames and route names)
 *
 * Used in:
 * - Login flows (after successful authentication)
 * - Signup flows (after account creation and consent acceptance)
 * - Consent completion (both signup and JIT consents)
 *
 * @returns The path to redirect to (either saved originalPath or feature-based landing)
 */
export const getPostAuthRedirectPath = (): string => {
  const originalPath = getAndClearOriginalPath();

  return originalPath || getLandingPageUrl() || '';
};

/**
 * Initialises the login process from an app perspective
 * Sets user and company into the redux store
 *
 * Fetching the user is critical - if it fails we cannot log in and show the troubleshooting panel.
 * Fetching company data is non-critical - if it fails we log the error and continue so that
 * shared accounts (where company info may not be important) can still log in.
 */
export const initUnifiedLogin = async () => {
  const { dispatch } = getStore();

  // Critical: fetch user data. If this fails we cannot proceed with login.
  let user;
  try {
    const { body } = await fetch('/user/me?exclude_field=badges', {
      body: undefined,
      method: 'get',
      oauth2token: true,
    });
    user = body;
  } catch (error) {
    AppLogger.logWithOptions({
      message:
        'Error fetching user during initUnifiedLogin - treating as unexpected auth error',
      level: LogTypes.Error,
      error,
      shouldLogToDatadog: true,
      shouldLogToDynatrace: true,
    });
    // Use isHard: false so the user is redirected to the Hermes login page
    // where the TroubleshootingPanel is shown, rather than to an SSO logout endpoint.
    // The backend session may still be valid but showing the panel takes priority.
    await handleUnexpectedAuthError({ isHard: false });
    return;
  }

  const companyId = user?.company_id;

  if (companyId) {
    // Non-critical: fetch and set company data. Log errors but continue login.
    try {
      const company = await fetchCompany(user.company_id);
      setCompany(company.body, true);
      DynatraceClient.sendSessionProperty('companyid', company.body.id);
    } catch (error) {
      AppLogger.logWithOptions({
        message:
          'Error fetching / setting company during initUnifiedLogin - continuing login without company data',
        level: LogTypes.Error,
        error,
        shouldLogToDatadog: true,
        shouldLogToDynatrace: true,
      });
    }
  } else {
    AppLogger.logWithOptions({
      message:
        'User does not have a company_id - continuing login without company data',
      level: LogTypes.Error,
      error: new Error('No company_id on user object'),
      shouldLogToDatadog: true,
      shouldLogToDynatrace: true,
    });
  }

  // Non-critical: update language. Log errors but continue login.
  try {
    await handleLangUpdateDuringLogin(user);
  } catch (error) {
    AppLogger.logWithOptions({
      message:
        'Error updating language during initUnifiedLogin - continuing login',
      level: LogTypes.Error,
      error,
      shouldLogToDatadog: true,
      shouldLogToDynatrace: true,
    });
  }

  AppLogger.logWithOptions({
    message: `User signup_state during initUnifiedLogin: ${user['signup_state']}`,
    context: { signup_state: user['signup_state'] },
    shouldLogToDatadog: true,
    shouldLogToDynatrace: true,
  });

  if (user['signup_state'] === SIGNUP_CODES.SSO_SIGNUP_PROVISIONED) {
    // An SSO user who has been auto-provisioned and needs to complete the signup flow
    // strictly they only need to accept consents but we only show consents after they signup form has been completed
    try {
      dispatch(
        updateData('initData', {
          user,
          signup_state: user['signup_state'],
        }),
      );

      // Defer post-login flow (including consents) until after the user completes the signup form
      // This prevents the consent modal or walkthrough from appearing over the signup form during IDP-initiated SSO
      await dispatch(shouldResumePostLoginFlow(true));
      goTo('signup');
    } catch (error) {
      AppLogger.logWithOptions({
        message:
          'Error dispatching signup state during initUnifiedLogin for SSO_SIGNUP_PROVISIONED user',
        level: LogTypes.Error,
        error,
        shouldLogToDatadog: true,
        shouldLogToDynatrace: true,
      });
      // Use isHard: false so the user lands on the Hermes login page with the
      // TroubleshootingPanel, rather than being redirected to an SSO logout endpoint.
      await handleUnexpectedAuthError({ isHard: false });
      return;
    }
  } else if (user['signup_state'] === SIGNUP_CODES.PENDING_CONSENT) {
    // A user who has completed Sign Up but needs to accept consents.
    // Critical: setUser must succeed for the app to function correctly.
    try {
      await setUser(user);
    } catch (error) {
      AppLogger.logWithOptions({
        message:
          'Error calling setUser during initUnifiedLogin (PENDING_CONSENT)',
        level: LogTypes.Error,
        error,
        shouldLogToDatadog: true,
        shouldLogToDynatrace: true,
      });
      // Use isHard: false so the user lands on the Hermes login page with the
      // TroubleshootingPanel, rather than being redirected to an SSO logout endpoint.
      await handleUnexpectedAuthError({ isHard: false });
      return;
    }

    await dispatch(shouldResumePostLoginFlow(true));

    // Set redirection model for consent container
    dispatch(
      setRedirectionModel({
        type: 'POP_UP',
        onCompleteGoTo: {
          name: getPostAuthRedirectPath(),
          params: {},
        },
      }),
    );

    // Redirect to sign-up consents page
    goTo(SIGN_UP_CONSENT_MODULE_NAME);
  } else {
    /*
     * Note that we only update features / set user if the user is NOT in the SSO signup provisioned state because that state should not see the navigation menu.
     * Although the features list we pass here is used to build the menu,
     * the permissions for the menu are checked against the user object from the Redux store.
     * This could perhaps be improved when we come to remove the legacy login,
     * as it is not obvious that we need to both set the user and THEN pass the same features list to rebuild the menu.
     */

    // Critical: setUser must succeed for the app to function correctly.
    try {
      await setUser(user);
    } catch (error) {
      AppLogger.logWithOptions({
        message: 'Error calling setUser during initUnifiedLogin',
        level: LogTypes.Error,
        error,
        shouldLogToDatadog: true,
        shouldLogToDynatrace: true,
      });
      // Use isHard: false so the user lands on the Hermes login page with the
      // TroubleshootingPanel, rather than being redirected to an SSO logout endpoint.
      await handleUnexpectedAuthError({ isHard: false });
      return;
    }

    if (user.features_list?.length > 0) {
      await dispatch(updateFeaturesList(user.features_list));

      const enabledFeatures = user.features_list
        .filter(feature => feature.value)
        .map(feature => feature.name);

      DynatraceClient.sendSessionProperty('featureslist', enabledFeatures);
    }

    // Non-critical: redirect to a saved pathname if it exists.
    // If getAndClearOriginalPath or goTo throws, log and continue — the user is logged in.
    try {
      const originalPath = getAndClearOriginalPath();
      if (originalPath) {
        goTo(originalPath);
      }
    } catch (error) {
      AppLogger.logWithOptions({
        message:
          'Error redirecting to original path during initUnifiedLogin - continuing login',
        level: LogTypes.Error,
        error,
        shouldLogToDatadog: true,
        shouldLogToDynatrace: true,
      });
    }

    runOnWebWorker(startPostLoginFlow);
  }

  await dispatch(setSessionLoadingCompleted());
};

/**
 * Sets the value to localStorage
 * @param isUnifiedLoginEnabled
 */
export const setUnifiedLoginEnabled = (isUnifiedLoginEnabled: boolean) => {
  localStorage.setItem(
    Storage.storageKeys.isUnifiedLoginEnabled,
    JSON.stringify(isUnifiedLoginEnabled),
  );
};

/**
 * If we are in a production environment, we will assume that USL is enabled
 * If we are in a non-production environment, we will check if the URL parameter 'isUnifiedLoginEnabled' exists
 * If there is no URL parameter, we will use a LocalStorage value
 */
/* IMPORTANT */
// As of May 2026 we are ignoring this check and assuming USL is enabled for all environments.
// For now, isUnifiedLoginEnabled() in client/app/globals/testable.tsx will always return true
// A full cleanup that removes this function and all related URL parameter and localStorage logic will be done in a future PR after USL Sign Up is fully rolled out and stable, and we can clean out all the old auth, signup and consent flows in one update.
export const getIsUnifiedLoginEnabled = (): boolean => {
  const urlParams = queryString.parse(location.search, { parseBooleans: true });
  const isUnifiedLoginEnabledParam = urlParams['isUnifiedLoginEnabled'];

  let effectiveIsUnifiedLoginEnabled = true;
  try {
    const isEnabledInProduction = true;
    if (IS_PRODUCTION) {
      return isEnabledInProduction;
    }
    // Check if we have a URL parameter that overrides the default value
    if (isUnifiedLoginEnabledParam !== undefined) {
      const paramValue =
        isUnifiedLoginEnabledParam === 'true' ||
        isUnifiedLoginEnabledParam === '"true"' ||
        isUnifiedLoginEnabledParam === "'true'";

      return paramValue;
    }
    // If no URL parameter, check localStorage
    const localStorageUslEnabled = localStorage.getItem(
      Storage.storageKeys.isUnifiedLoginEnabled,
    );

    if (localStorageUslEnabled !== null) {
      effectiveIsUnifiedLoginEnabled =
        localStorageUslEnabled === 'true' ||
        localStorageUslEnabled === '"true"' ||
        localStorageUslEnabled === "'true'";
    }
    return effectiveIsUnifiedLoginEnabled;
  } catch (e) {
    // If anything fails in this function, assume USL is enabled
    AppLogger.logWithOptions({
      message: 'Error in getIsUnifiedLoginEnabled, defaulting to USL enabled',
      level: LogTypes.Error,
      error: e,
      shouldLogToDatadog: true,
      shouldLogToDynatrace: true,
    });
    return true;
  }
};

/**
 * Performs the tenancy lookup based on the provided key and value
 * The response is used to set the correct regional API endpionts for further requests
 * This is why we want to ensure that the tenancy lookup is performed before any other API calls
 * If we have an existing tenancyHash in localStorage, we will use that to perform the lookup
 * Otherwise, we will use the subdomain from the host
 * If the tenancy lookup fails, we will log the user out
 */
export const callTenancyLookup = async () => {
  try {
    const tenancyHash = localStorage.getItem(lookupKeys.tenancyHash);
    let lookUpResponse;

    // Make a call to the tenancy lookup API to set the correct endpoints
    if (tenancyHash) {
      lookUpResponse = await performTenancyLookup(
        lookupKeys.tenancyHash,
        tenancyHash,
      );
    } else {
      // If tenancyHash is not set, we will use the domain to perform the lookup
      lookUpResponse = await performTenancyLookup(
        lookupKeys.domain,
        getSubdomainFromHost(),
      );
    }
    if (!lookUpResponse) {
      AppLogger.logWithOptions({
        message: 'Tenancy lookup failed, no response received',
        level: LogTypes.Error,
        shouldLogToDatadog: true,
        shouldLogToDynatrace: true,
      });
      await handleUnexpectedAuthError({ isHard: false });
    }
  } catch (error) {
    AppLogger.logWithOptions({
      message: 'Error in performTenancyLookup',
      level: LogTypes.Error,
      error: error,
      shouldLogToDatadog: true,
      shouldLogToDynatrace: true,
    });
    await handleUnexpectedAuthError({ isHard: false });
  }
};
